import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X, Share, Plus, MoreVertical, MonitorDown } from "lucide-react";
import { Button } from "./ui/button";

const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (iOS) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
};

export const InstallButton = ({ className = "" }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [help, setHelp] = useState(false);
  const [platform, setPlatform] = useState("desktop");

  useEffect(() => {
    setPlatform(detectPlatform());
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setHelp(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) setInstalled(true);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  useEffect(() => {
    if (!help) return;
    const onKey = (e) => { if (e.key === "Escape") setHelp(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [help]);

  if (installed) return null;

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else {
      setHelp(true);
    }
  };

  const label = platform === "ios" ? "Ajouter à l'accueil" : "Installer l'app";

  return (
    <>
      <Button onClick={click} className={`rounded-full bg-fresh-mint hover:bg-fresh-mint/90 text-white font-bold gap-1.5 ${className}`} data-testid="install-btn" aria-label={`${label} StreatEat`}>
        <Download size={16} aria-hidden="true" /> {label}
      </Button>
      {help && createPortal(
        <div className="fixed inset-0 bg-urban-teal/50 z-[100] flex items-center justify-center p-4" onClick={() => setHelp(false)} data-testid="install-help" role="presentation">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="install-help-title">
            <div className="flex items-center justify-between mb-3"><p id="install-help-title" className="font-display font-bold text-urban-teal">Installer StreatEat</p><button onClick={() => setHelp(false)} className="text-urban-teal/60 hover:text-urban-teal p-1 -mr-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-street-gold" data-testid="install-help-close" aria-label="Fermer la fenêtre"><X size={22} aria-hidden="true" /></button></div>

            {platform === "ios" && (
              <ol className="space-y-3 text-sm text-urban-teal/80" data-testid="install-help-ios">
                <li className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-cream-bg flex items-center justify-center font-bold text-urban-teal shrink-0">1</span> Appuyez sur <Share size={16} className="text-street-gold" /> <b>Partager</b> dans la barre de Safari.</li>
                <li className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-cream-bg flex items-center justify-center font-bold text-urban-teal shrink-0">2</span> Choisissez <Plus size={16} className="text-street-gold" /> <b>Sur l'écran d'accueil</b>.</li>
                <li className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-cream-bg flex items-center justify-center font-bold text-urban-teal shrink-0">3</span> Appuyez sur <b>Ajouter</b>. StreatEat apparaît comme une app !</li>
              </ol>
            )}

            {platform === "android" && (
              <ol className="space-y-3 text-sm text-urban-teal/80" data-testid="install-help-android">
                <li className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-cream-bg flex items-center justify-center font-bold text-urban-teal shrink-0">1</span> Ouvrez le menu <MoreVertical size={16} className="text-street-gold" /> en haut à droite.</li>
                <li className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-cream-bg flex items-center justify-center font-bold text-urban-teal shrink-0">2</span> Touchez <b>Installer l'application</b> (ou « Ajouter à l'écran d'accueil »).</li>
                <li className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-cream-bg flex items-center justify-center font-bold text-urban-teal shrink-0">3</span> Confirmez - l'app s'installe automatiquement.</li>
              </ol>
            )}

            {platform === "desktop" && (
              <ol className="space-y-3 text-sm text-urban-teal/80" data-testid="install-help-desktop">
                <li className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-cream-bg flex items-center justify-center font-bold text-urban-teal shrink-0">1</span> Cliquez sur l'icône <MonitorDown size={16} className="text-street-gold" /> à droite de la barre d'adresse.</li>
                <li className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-cream-bg flex items-center justify-center font-bold text-urban-teal shrink-0">2</span> Cliquez sur <b>Installer</b> dans la fenêtre qui s'ouvre.</li>
                <li className="text-xs text-urban-teal/50 pl-10">Astuce : disponible sur Chrome, Edge et Brave. Sur Firefox/Safari, ajoutez la page à vos favoris.</li>
              </ol>
            )}

            <Button onClick={() => setHelp(false)} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold mt-5 w-full" data-testid="install-help-ok">Compris</Button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
